import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { LeadForm } from "@/components/LeadForm";
import { Layers, GitBranch, TrendingUp, Lock } from "lucide-react";

const IMG =
  "https://images.unsplash.com/photo-1777465150936-239ca191963e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const steps = [
  { icon: Layers, title: "SPV dedicata", desc: "Ogni operazione vive in una società veicolo (SPV) autonoma: perimetro chiaro, rischi isolati, contabilità separata." },
  { icon: GitBranch, title: "Club deal", desc: "Un gruppo ristretto di investitori partecipa alla singola operazione, con quote e diritti definiti da patti chiari." },
  { icon: TrendingUp, title: "Hurdle rate & carried", desc: "Il nostro carried interest scatta solo sopra un rendimento minimo (hurdle rate) riconosciuto prima a te." },
  { icon: Lock, title: "Reporting riservato", desc: "Accesso a documentazione e reportistica periodica, con ipotesi esplicite e nessuna zona grigia." },
];

export default function Investitori() {
  useSeo({
    title: "Investitori & Club Deal immobiliari",
    description:
      "Club deal immobiliari per HNWI, Family Office e aziende: struttura SPV, carried interest sopra hurdle rate e allineamento reale. Richiedi l'Investment Memorandum.",
  });

  return (
    <div>
      <section className="relative pt-20 pb-20 overflow-hidden noise-overlay border-b border-white/5">
        <div className="absolute inset-0 z-0">
          <img src={IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0D1B2A]/90" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-5 lg:px-8">
          <span className="label-eyebrow">Investitori / Club Deal</span>
          <Reveal>
            <h1 className="font-display font-light text-4xl lg:text-6xl text-white mt-4 max-w-3xl leading-tight">
              Capitale allineato, non solo raccolto
            </h1>
          </Reveal>
          <p className="text-gray-300 mt-6 max-w-2xl text-lg leading-relaxed">
            Strutturazione di operazioni immobiliari dedicate con partecipazione diretta all'asset e
            governance trasparente.
          </p>
        </div>
      </section>

      <section className="bg-[#1E2229] py-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <span className="label-eyebrow">Come funziona il modello</span>
          <h2 className="font-display font-light text-3xl lg:text-4xl text-white mt-4 max-w-2xl">
            SPV, club deal e carried interest in parole semplici
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px mt-14 bg-white/5">
            {steps.map((s, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="bg-[#1E2229] p-8 h-full">
                  <s.icon className="text-[#C5A059]" size={30} strokeWidth={1.5} />
                  <h3 className="font-display text-lg text-white mt-5">{s.title}</h3>
                  <p className="text-sm text-gray-400 mt-3 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-8 max-w-3xl">
            Le informazioni in questa pagina hanno finalità illustrative e non costituiscono
            sollecitazione all'investimento né offerta di prodotti finanziari. I target di
            rendimento non sono garantiti.
          </p>
        </div>
      </section>

      <section className="bg-[#0D1B2A] py-24">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Reveal>
            <span className="label-eyebrow">Accesso riservato</span>
            <h2 className="font-display font-light text-3xl lg:text-4xl text-white mt-4">
              Richiedi l'Investment Memorandum
            </h2>
            <p className="text-gray-400 mt-4 leading-relaxed">
              Compila il modulo riservato. Nessun dato sensibile: solo le informazioni essenziali per
              qualificare la richiesta e inviarti la documentazione riservata.
            </p>
            <div className="mt-10">
              <LeadForm
                fixedProfile="Investitore"
                businessLine="Advisory & Club Deal"
                sourcePage="Investitori / Club Deal"
                showInvestorFields
                submitLabel="Richiedi il Memorandum"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#1E2229] py-12 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <p className="text-xs text-gray-500 leading-relaxed">
            <strong className="text-gray-400">Avvertenza (TUF).</strong> Le opportunità illustrate non
            costituiscono offerta al pubblico di prodotti finanziari né sollecitazione all'investimento
            ai sensi del TUF (D.Lgs. 58/1998). Ogni operazione è strutturata tramite veicoli societari
            dedicati (SPV) o contratti di partecipazione privata tra soci/partner professionali.
          </p>
        </div>
      </section>
    </div>
  );
}
